/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commerical BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2020-03-19 00:14:33
 *
 *******************************************************/
// @flow

import _ from 'lodash';
// import { SubmissionError } from 'redux-form';

class MultiLevelTagHelper {
  /**
   * Init values from first loading
   *
   * @param  {object} filterOption  filter option value
   * @param  {object} initialValue default values
   *
   * @return {object}               init values
   */
  static initialValues = (filterOption, initialValue) => {
    const { manualValues, useAndCondition, selectType } = filterOption;

    // Define parse function
    const funcParseTag = tagItem => {
      const { tag, displayName, subTags = [] } = tagItem;
      if (subTags.length > 0) {
        return {
          tag: { value: tag, label: tag },
          displayName: displayName,
          subTags: subTags.map(subTag => funcParseTag(subTag))
        };
      } else {
        return {
          tag: { value: tag, label: tag },
          displayName: displayName
        };
      }
    };

    let multiLevelTagManualValues = [];
    multiLevelTagManualValues = manualValues ? manualValues.map(item => funcParseTag(item)) : [];

    const result = {
      ...initialValue,
      multiLevelTagManualValues,
      useAndCondition,
      multiLevelTagOptionSelect: selectType
    };

    return result;
  };

  /**
   * Build filter option data based on input values
   *
   * @param  {object} filterOption    input values
   * @param  {object} defaultValues   default values
   *
   * @return {object}                 filter option values
   */
  static buildFilterOptionValues = (filterOption, defaultValues) => {
    const { multiLevelTagManualValues, useAndCondition, multiLevelTagOptionSelect } = filterOption;

    // Define parse function
    const funcParseTag = tagItem => {
      const { tag, displayName, subTags = [] } = tagItem;
      const value = _.get(tag, 'value', '');
      if (_.isEmpty(value)) return null;

      return subTags.length > 0
        ? {
            tag: _.get(tag, 'value', ''),
            displayName,
            subTags: subTags.map(subTag => funcParseTag(subTag)).filter(subTag => subTag)
          }
        : {
            tag: _.get(tag, 'value', ''),
            displayName
          };
    };

    let manualValues = null;
    manualValues = multiLevelTagManualValues
      ? multiLevelTagManualValues
          .map(item => {
            return funcParseTag(item);
          })
          .filter(item => item)
      : null;

    const result = {
      ...defaultValues,
      displayType: 'list',
      selectType: multiLevelTagOptionSelect,
      valueType: 'specific',
      manualValues,
      useAndCondition,
      removeTextFilterValues: '',
      replaceTextFilterValues: null
    };

    return result;
  };

  /**
   * Validate for tag
   *
   * @param  {object} filterOption
   * @return {boolean/error} void if well validated, throw error if have
   */
  static validateFilterOption = filterOption => {
    return;
  };

  /**
   * Remove no props for comparaison
   *
   * @param  {array} list list tag
   * @return {array}      updated list
   */
  static removeEmptyNoCompareProps = list => {
    const cloned = _.cloneDeep(list);

    // Define parse function
    const funcParseTag = tagItem => {
      const { tag, displayName, subTags = [] } = tagItem;

      // Delete expanded
      delete tagItem.expanded;

      // Process sub tags
      if (subTags.length > 0)
        return { tag, displayName, subTags: subTags.map(subTag => funcParseTag(subTag)) };

      // Delete subTags if empty array
      delete tagItem.subTags;
      return tagItem;
    };

    return cloned.map(item => {
      return funcParseTag(item);
    });
  };

  /**
   * Check selected manual values is empty
   *
   * @param  {array} manualValues
   *
   * @return {boolean}              true | false
   */
  static isEmptyMultiLevelManualValues = manualValues => {
    if (_.isEmpty(manualValues) || (_.isArray(manualValues) && manualValues.length === 0))
      return true;

    const manualValue = _.get(manualValues, '[0]', {});
    if (_.isArray(manualValues) && manualValues.length === 1 && _.isEmpty(manualValue.tag)) {
      return true;
    }

    return false;
  };

  /**
   * Remove selected tags
   *
   * @param  {array} inititalTags list of all tags
   * @param  {array} manualValues
   *
   * @return {array}              list of tags after removing selected tag
   */
  static removeSelectedTags = (inititalTags, manualValues) => {
    const tags = _.cloneDeep(inititalTags);

    const removeTag = tagItem => {
      const { tag, subTags = [] } = tagItem;

      // Remove tag
      _.remove(tags, { value: tag.value });

      subTags.forEach(subTagItem => {
        removeTag(subTagItem);
      });
    };

    manualValues.forEach(tagItem => {
      removeTag(tagItem);
    });

    return tags;
  };

  /**
   * Get selected tags
   * @param {*} manualValues
   * @returns list of tags
   */
  static getSelectedTags = (manualValues, source = 'multi-level-tags') => {
    const selectedTags = [];

    const collectTags = tagItem => {
      const { tag, subTags = [] } = tagItem;
      if (!_.isEmpty(tag)) {
        selectedTags.push(tag);
      }

      subTags.forEach(subTagItem => {
        collectTags(subTagItem);
      });
    };

    if (source === 'multi-level-collections') {
      // Sample data: [{ handle, tags: [ tag, subTags: [ { label, value }, { label, value } ] ] } ]
      manualValues.forEach(collectionItem => {
        const { tags = [] } = collectionItem;
        tags.forEach(tagItem => {
          collectTags(tagItem);
        });
      });
    } else {
      // Sample data: [ { tag: { label, value }, subTags: [ tag: { label, value }, subTags: [] ] } ]
      manualValues.forEach(tagItem => {
        collectTags(tagItem);
      });
    }

    return selectedTags;
  };
}

export default MultiLevelTagHelper;
